// FR-03's hot cues and FR-04's loop controls (P3.10).
//
// Deck-scoped rather than in `/ui/pads`, which Architecture §11 reserves for M6's sampler grid.
// These are transport controls: a hot cue moves *this* deck's playhead and a loop is *this* deck's
// boundaries. They live with the transport for the same reason the pitch fader does.
//
// **Nothing here decides anything.** A pad reports a press; the audio thread sets the cue at
// whatever the playhead is, computes the loop's boundaries from the grid it holds, and publishes
// both back. This component renders what came back — the same inversion `Transport.tsx` describes
// for play and cue, and for the same reason: the side that owns the state is the side that knows it.

import { useEffect, useRef, type JSX } from 'react';

import {
  AUTO_LOOP_BEATS,
  HOT_CUE_COUNT,
  isSetCue,
  type LoopQuantization,
} from '../../platform/types/loop';
import type { TrackLoaderApi } from './useTrackLoader';

/**
 * What the quantization state says on screen.
 *
 * P3.10's rule: a track with no grid, or a low-confidence one, "must still loop — unquantized, and
 * **honestly so**". Honestly means the user can tell *before* they use it, and can tell the two
 * refusals apart: "no tempo was found" and "a tempo was found and the detector was guessing" want
 * different reactions — the second is what FR-13's manual grid tools are for.
 */
export function describeQuantization(quantization: LoopQuantization): string {
  switch (quantization) {
    case 'beat':
      return 'snapping to the beat';
    case 'unquantized-no-grid':
      return 'free — no beatgrid on this track';
    case 'unquantized-low-confidence':
      return 'free — the tempo is a guess, so loops are not snapped';
  }
}

export interface PerformancePadsProps {
  readonly loader: TrackLoaderApi;
}

export function PerformancePads({ loader }: PerformancePadsProps): JSX.Element {
  const deck = loader.deckId.toUpperCase();
  const regionRef = useRef<HTMLSpanElement | null>(null);
  const { readLoop } = loader;

  // The boundaries belong to the audio thread, so they are polled at display rate and written
  // straight to the DOM — Stack §2, the same rule the meters and the phase error follow.
  useEffect(() => {
    let frame = 0;
    const tick = (): void => {
      frame = requestAnimationFrame(tick);
      const node = regionRef.current;
      if (node === null) {
        return;
      }
      const region = readLoop();
      node.textContent =
        region === null
          ? 'off'
          : `${region.startSeconds.toFixed(3)}s → ${region.endSeconds.toFixed(3)}s`;
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [readLoop]);

  return (
    <div className="stack stack--tight">
      {/* FR-03: eight, and the count comes from the constant rather than a literal so the row and
          the audio thread's array cannot disagree. */}
      <span className="micro">Hot cues</span>
      <div className="pads">
        {Array.from({ length: HOT_CUE_COUNT }, (_unused, slot) => {
          const set = isSetCue(loader.hotCues[slot] ?? Number.NaN);
          return (
            <button
              key={slot}
              type="button"
              className={`pads__pad${set ? ' pads__pad--set' : ''}`}
              // A pad does two jobs and the distinction is the one every DJ controller makes:
              // pressing an empty pad sets a cue, pressing a set one jumps to it. Shift-click (or a
              // right-click, for a trackpad) clears — destructive, so it is never the plain press.
              onClick={(event) => {
                if (event.shiftKey) {
                  loader.hotCue('clear', slot);
                } else {
                  loader.hotCue(set ? 'jump' : 'set', slot);
                }
              }}
              onContextMenu={(event) => {
                event.preventDefault();
                loader.hotCue('clear', slot);
              }}
              aria-label={
                set
                  ? `Jump to hot cue ${String(slot + 1)} on deck ${deck} (shift-click to clear)`
                  : `Set hot cue ${String(slot + 1)} on deck ${deck}`
              }
              aria-pressed={set}
            >
              {slot + 1}
            </button>
          );
        })}
      </div>

      <span className="micro">
        Loop — {describeQuantization(loader.loopQuantization)}
      </span>
      <div className="row row--tight">
        {AUTO_LOOP_BEATS.map((beats) => (
          <button
            key={beats}
            type="button"
            onClick={() => {
              loader.loop('auto', beats);
            }}
            // Auto loops are measured in beats, so a track with no grid has no length to make one
            // from. Manual in/out below still works, which is FR-04's "must still loop".
            disabled={loader.loopQuantization === 'unquantized-no-grid'}
            aria-label={`Loop ${String(beats)} beats on deck ${deck}`}
          >
            {beats}
          </button>
        ))}
      </div>

      <div className="row row--tight">
        <button
          type="button"
          onClick={() => {
            loader.loop('in');
          }}
          aria-label={`Set loop in point on deck ${deck}`}
        >
          IN
        </button>
        <button
          type="button"
          onClick={() => {
            loader.loop('out');
          }}
          aria-label={`Set loop out point on deck ${deck}`}
        >
          OUT
        </button>
        {/* Exit and reloop are two buttons rather than one toggle, because they are not opposites:
            exit leaves the playhead where it is and plays on, while reloop jumps back to the loop's
            start. A toggle would have to pick one meaning for the second press. */}
        <button
          type="button"
          className={loader.loopActive ? 'is-active' : undefined}
          onClick={() => {
            loader.loop('exit');
          }}
          disabled={!loader.loopActive}
          aria-label={`Exit the loop on deck ${deck}`}
        >
          EXIT
        </button>
        <button
          type="button"
          onClick={() => {
            loader.loop('reloop');
          }}
          aria-label={`Return to the last loop on deck ${deck}`}
        >
          RELOOP
        </button>
        <span ref={regionRef} className="mono loop__state" role="status">
          off
        </span>
      </div>
    </div>
  );
}
